import { APPOINTMENTS_SCHEDULE_DAYS } from '@avanta/shared/constants/common';
import { type AvailableDates } from '@avanta/shared/types/appointments';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router';
import { QUERY_KEYS } from 'constants/common';
import { AppointmentService } from 'services/appointments';
import { type DailySlot } from 'types/appointments';
import { getCurrentDateISO, getDateAfterDays } from 'utils/date';
import { getUserId } from 'utils/storage';
import { AppointmentsReservePayload } from '../../../types';
import { getAvailableDatesFromSlots } from './utils';

interface UseFetchPsychologistAvailabilityReturn {
  dailySlots: DailySlot[] | undefined;
  availableDates: AvailableDates;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

export const useFetchPsychologistAvailability = (): UseFetchPsychologistAvailabilityReturn => {
  const { state } = useLocation();
  const startDate = getCurrentDateISO();
  const endDate = getDateAfterDays(APPOINTMENTS_SCHEDULE_DAYS);
  const patientId = state?.patientId ?? getUserId();
  const { watch } = useFormContext();
  const [backofficeCenterId, specialityId, psychologist] = watch([
    AppointmentsReservePayload.backofficeCenterId,
    AppointmentsReservePayload.specialityBooked,
    AppointmentsReservePayload.psychologist,
  ]);

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [QUERY_KEYS.FETCH_PSYCHOLOGIST_AVAILABILITY, patientId],
    queryFn: () =>
      AppointmentService.getPsychologistAvailability({
        patientId,
        centerId: backofficeCenterId,
        specialityId: specialityId,
        startDate,
        endDate,
        psychologistId: psychologist.id,
      }),
    select: response => response.data?.[0],
    enabled: !!specialityId && !!psychologist.id && !!backofficeCenterId,
  });

  const availableDates = useMemo(
    () => (data?.dailySlots ? getAvailableDatesFromSlots(data.dailySlots) : {}),
    [data]
  );

  return {
    dailySlots: data?.dailySlots,
    availableDates,
    isLoading,
    isFetching,
    isError,
  };
};
